import { useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Instagram, Music2 } from "lucide-react";
import { CountUp } from "@/components/site/CountUp";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES, METRICS, AMBASSADORS } from "@/data/site";

const SpinNumber = ({ value, suffix, color }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduce = useReducedMotion();
  return (
    <motion.span
      ref={ref}
      className="inline-block font-heading text-6xl font-black leading-none tracking-tight md:text-7xl"
      style={{ color }}
      animate={inView && !reduce ? { rotate: [0, 360] } : {}}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    >
      {value}
      {suffix}
    </motion.span>
  );
};

const WipeLink = ({ icon: Icon, label, inverse = false, href }) => (
  <a
    href={href || "#"}
    target={href ? "_blank" : undefined}
    rel={href ? "noopener noreferrer" : undefined}
    onClick={(e) => {
      if (!href) e.preventDefault();
    }}
    data-cursor-hover
    className={`group relative inline-flex items-center gap-2 text-sm font-bold ${inverse ? "text-white" : "text-ink"}`}
  >
    <Icon size={16} />
    <span className="relative">
      {label}
      <span
        className={`absolute -bottom-0.5 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100 ${inverse ? "bg-white" : "bg-ink"}`}
      />
    </span>
  </a>
);

const AmbassadorCard = ({ amb, index }) => {
  const [flip, setFlip] = useState(false);
  const reduce = useReducedMotion();
  return (
    <Reveal delay={index * 0.08} className="h-full [perspective:1400px]">
      <div
        data-testid={`ambassador-${index}`}
        className="relative h-[440px] w-full cursor-pointer"
        onMouseEnter={() => setFlip(true)}
        onMouseLeave={() => setFlip(false)}
        onClick={() => setFlip((f) => !f)}
      >
        <motion.div
          className="relative h-full w-full [transform-style:preserve-3d]"
          animate={{ rotateY: reduce ? 0 : flip ? 180 : 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* front — full-bleed photo + big TikTok follower count */}
          <div className="absolute inset-0 overflow-hidden border border-hair [backface-visibility:hidden]">
            {amb.photo ? (
              <img
                src={amb.photo}
                alt={amb.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: amb.color }}
              >
                <span className="font-heading text-[140px] font-black leading-none text-white/90">
                  {amb.initial}
                </span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 top-1/3 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
            <span
              className="absolute left-0 top-0 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white"
              style={{ backgroundColor: amb.color }}
            >
              {amb.name}
            </span>
            <div className="absolute inset-x-0 bottom-0 p-6">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/70">
                {amb.ttCount ? "TikTok" : "Instagram"}
              </span>
              <div className="font-heading text-4xl font-black leading-none text-white break-words">
                {amb.ttCount || amb.igHandle}
              </div>
              <span className="mt-2 block text-xs text-white/70">
                {amb.ttCount ? "obserwujących na TikToku" : "obserwuj na Instagramie"}
              </span>
            </div>
          </div>
          {/* back — detailed stats + links */}
          <div
            className="absolute inset-0 flex flex-col justify-between border p-8 [backface-visibility:hidden] [transform:rotateY(180deg)]"
            style={{ backgroundColor: amb.color, borderColor: amb.color }}
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                {amb.name}
              </span>
              <div className="mt-5 font-heading text-4xl font-black leading-none text-white break-words">
                {amb.topStat}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/90">{amb.topLabel}</p>
              <p className="mt-6 text-sm leading-relaxed text-white/90">{amb.ig}</p>
              {amb.tt && <p className="mt-1 text-sm leading-relaxed text-white/90">{amb.tt}</p>}
            </div>
            <div className="flex items-center gap-6" onClick={(e) => e.stopPropagation()}>
              <WipeLink icon={Instagram} label="Instagram" inverse href={amb.igUrl} />
              {amb.ttCount && <WipeLink icon={Music2} label="TikTok" inverse />}
            </div>
          </div>
        </motion.div>
      </div>
    </Reveal>
  );
};

export const Metrics = () => {
  const reduce = useReducedMotion();
  const headerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-40, 40]);

  return (
    <section id="metrics" data-testid="section-metrics" className="border-t border-hair py-12 md:py-20">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div ref={headerRef} className="relative overflow-hidden">
          <motion.div
            aria-hidden
            style={{ y: bgY, backgroundImage: `url(${IMAGES.metrics})` }}
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.06]"
          />
          <div className="relative">
            <Reveal>
              <span className="eyebrow">Zasięg</span>
              <h2 className="mt-4 font-heading text-4xl font-bold leading-[1.02] tracking-tight text-ink md:text-6xl">
                Ogólnopolska Kampania
              </h2>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 border border-hair sm:grid-cols-3">
              {METRICS.map((m, i) => (
                <Reveal
                  key={i}
                  delay={i * 0.08}
                  className={`p-10 ${i < 2 ? "border-b border-hair sm:border-b-0 sm:border-r" : ""}`}
                >
                  {m.spin ? (
                    <SpinNumber value={m.value} suffix={m.suffix} color={m.color} />
                  ) : (
                    <div
                      className="font-heading text-6xl font-black leading-none tracking-tight md:text-7xl"
                      style={{ color: m.color }}
                    >
                      <CountUp end={m.value} suffix={m.suffix} />
                    </div>
                  )}
                  <p className="mt-6 text-sm font-bold uppercase tracking-[0.14em] text-graphite">
                    {m.label}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Activation + Anthem */}
        <div className="mt-8 grid grid-cols-1 border border-hair md:grid-cols-2">
          <Reveal className="border-b border-hair p-8 md:border-b-0 md:border-r md:p-12">
            <span className="eyebrow" style={{ color: "#44A8C6" }}>
              Aktywacja TikTok
            </span>
            <p className="mt-6 text-base leading-relaxed text-ink md:text-lg">
              Ambasador rozpoczyna capsowe wyzwanie budowlane - jedynym materiałem są zakrętki i
              fantazja. Nominuje kolejnego ambasadora, a ten kolejnych influencerów. Najlepsi walczą o
              tytuł GOAT.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="p-8 md:p-12">
            <span className="eyebrow" style={{ color: "#F59BBB" }}>
              Hymn kampanii
            </span>
            <p className="mt-6 font-heading text-2xl font-bold text-ink md:text-3xl">
              „Moja Fantazja" - <span style={{ color: "#F59BBB" }}>FASOLKI</span>
            </p>
            <p className="mt-4 text-base leading-relaxed text-graphite">
              Kultowy polski utwór w rytmie fali nostalgii - uwielbiany przez rodziców, z potencjałem
              na wiralowy powrót wśród dzieci i nastolatków.
            </p>
          </Reveal>
        </div>

        {/* Ambassadors */}
        <div className="mt-16">
          <Reveal>
            <span className="eyebrow">Ambasadorzy</span>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
            {AMBASSADORS.map((a, i) => (
              <AmbassadorCard key={a.name} amb={a} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
